import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { invalidateAfter, keys } from "../api/query-registry";
import type { Todo } from "./useTodos";

export interface TodoTemplate {
  id: string;
  title: string;
  category: string | null;
  homeId: string | null;
  relativeTo: "none" | "admission" | "icp";
  relativeDays: number | null;
  recurrence: "none" | "daily" | "weekly" | "monthly" | "yearly";
  recurrenceDay: number | null;
  recurrenceWeekday: number | null;
  tags: string[];
  createdAt: string;
}

export interface CreateTemplate {
  title: string;
  category?: string | null;
  recurrence?: string | null;
  recurrenceDay?: number | null;
  recurrenceWeekday?: number | null;
  relativeTo?: string | null;
  relativeDays?: number | null;
  tags?: string[];
}

export function useTemplates() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: keys.templates(), queryFn: () => api<TodoTemplate[]>("/api/templates"), staleTime: 30_000 });
  const invalidate = () => invalidateAfter(qc, "templatesChanged");
  const create = useMutation({
    mutationFn: (p: CreateTemplate) => api<TodoTemplate>("/api/templates", { method: "POST", body: p }),
    onSuccess: invalidate,
  });
  const instantiate = useMutation({
    mutationFn: ({ id, dueDate }: { id: string; dueDate?: string | null }) =>
      api<Todo>("/api/templates/" + id + "/instantiate", { method: "POST", body: { dueDate: dueDate ?? null } }),
    onSuccess: () => invalidateAfter(qc, "todoChanged"),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/api/templates/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
  return { query, create, instantiate, remove };
}

